import type { StateContext, StateName } from './types';
import { devBus } from '../debug/devBus';

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

export type TargetChangeListener = (newTarget: StateName, oldTarget: StateName) => void;

export class StateController {
  current: StateName = 'past';
  target: StateName = 'past';
  progress = 1;
  duration = 1;

  private targetListeners: TargetChangeListener[] = [];
  // U4 — true while a scrub parks the morph at an intermediate value. tick()
  // must not advance a held progress toward settled (that would slide the
  // scrubbed morph back to a pole within a second). Any real setTarget clears
  // the hold and resumes normal timed transitions.
  private scrubHold = false;

  /** Subscribe to target changes. Returns an unsubscribe function. The
   *  InteractionEngine uses this to fire 'stateEnter' rules so music /
   *  ambient / lighting can swap as soon as a new state is selected, before
   *  the transition itself finishes. */
  onTargetChange(fn: TargetChangeListener): () => void {
    this.targetListeners.push(fn);
    return () => {
      const i = this.targetListeners.indexOf(fn);
      if (i >= 0) this.targetListeners.splice(i, 1);
    };
  }

  setTarget(target: StateName): void {
    // A deliberate transition resumes normal behavior: release any scrub hold
    // even on the early-return path, so a held mid-morph gently settles to its
    // pole over `duration` instead of staying parked forever (U4).
    this.scrubHold = false;
    if (target === this.target) return;
    const oldTarget = this.target;
    this.target = target;
    if (this.duration === 0) {
      this.current = target;
      this.progress = 1;
    } else {
      this.progress = 0;
    }
    for (const fn of this.targetListeners) fn(target, oldTarget);
    devBus.emit('state:target', { target });
  }

  setProgress(p: number): void {
    this.progress = clamp01(p);
    if (this.progress === 1) this.current = this.target;
    devBus.emit('state:progress', { progress: this.progress });
  }

  /** Scrub the morph to `m` (0 = past … 1 = present) and HOLD it there (U4).
   *  Replaces the broken `duration=0; setTarget('present'); setProgress(m)`
   *  idiom, which permanently zeroed the ⚙ morph duration, flipped `current`
   *  to 'present' regardless of where the morph actually sat (stranding music,
   *  era-gated cues, and both visual transitions on the wrong era), and killed
   *  the era toggle (setTarget early-returned forever after).
   *
   *  Here `current`/`target` stay coherent with the morph SIDE — they flip
   *  only when the scrub crosses 0.5 — and `duration` is never touched.
   *  Target listeners fire only on a real side flip (a scrub crossing IS an
   *  era arrival; per the fluidity rule content plays through it). The hold
   *  freezes tick() so the parked morph doesn't drift; the next setTarget
   *  (era toggle, cue state.set) releases it and transitions normally. */
  scrubMorph(m: number): void {
    m = clamp01(m);
    const side: StateName = m >= 0.5 ? 'present' : 'past';
    const oldTarget = this.target;
    this.current = side;
    this.target = side;
    // Morph readers compute: target === 'present' ? progress : 1 - progress.
    this.progress = side === 'present' ? m : 1 - m;
    this.scrubHold = this.progress < 1; // a pole needs no hold — it's settled
    if (side !== oldTarget) {
      for (const fn of this.targetListeners) fn(side, oldTarget);
      devBus.emit('state:target', { target: side });
    }
    devBus.emit('state:progress', { progress: this.progress });
  }

  tick(deltaSeconds: number): void {
    if (this.scrubHold) return; // U4: a parked scrub holds until the next setTarget
    if (this.progress < 1 && this.duration > 0) {
      this.progress = clamp01(this.progress + deltaSeconds / this.duration);
      if (this.progress === 1) this.current = this.target;
    }
  }

  get context(): StateContext {
    return {
      current: this.current,
      target: this.target,
      progress: this.progress,
    };
  }
}
